import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { getLeads, retryGhlDelivery } from "@/lib/leads.functions";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/leads")({
  head: () => ({ meta: [{ title: "Admin · Leads" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: LeadsPage,
});

type Range = "24h" | "7d" | "30d" | "all";
type StatusFilter = "all" | "delivered" | "failed" | "spam_blocked" | "not_attempted";

type Lead = {
  id: string;
  created_at: string;
  submitted_at: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  business_name: string | null;
  message: string | null;
  page: string | null;
  ghl_status: string;
  ghl_error: string | null;
  ghl_status_code: number | null;
  ghl_delivered_at: string | null;
};

type Stats = {
  total: number;
  delivered: number;
  failed: number;
  spam_blocked: number;
  not_attempted: number;
};

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  delivered: { label: "Delivered", variant: "default" },
  failed: { label: "Failed", variant: "destructive" },
  spam_blocked: { label: "Spam", variant: "secondary" },
  not_attempted: { label: "Not attempted", variant: "outline" },
};

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString();
}

function LeadsPage() {
  const navigate = useNavigate();
  const getLeadsFn = useServerFn(getLeads);
  const retryFn = useServerFn(retryGhlDelivery);

  const [range, setRange] = useState<Range>("7d");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats7d, setStats7d] = useState<Stats | null>(null);
  const [stats30d, setStats30d] = useState<Stats | null>(null);
  const [selected, setSelected] = useState<Lead | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  type RetryAttempt = {
    leadId: string;
    leadEmail: string | null;
    ok: boolean;
    status: number;
    statusText?: string;
    body: string;
    headers: Record<string, string>;
    startedAt: string;
    finishedAt: string;
    error?: string;
  };
  const [retryLog, setRetryLog] = useState<RetryAttempt[]>([]);
  const lastByLead: Record<string, RetryAttempt> = {};
  for (const a of retryLog) if (!lastByLead[a.leadId]) lastByLead[a.leadId] = a;


  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getLeadsFn({ data: { range, status, search: debouncedSearch, limit: 200 } })
      .then((res: any) => {
        if (cancelled) return;
        setLeads(res.leads as Lead[]);
        setStats7d(res.stats7d as Stats);
        setStats30d(res.stats30d as Stats);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message ?? "Failed to load leads");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, status, debouncedSearch, reloadKey, getLeadsFn]);

  async function onRetry(id: string, email: string | null) {
    setRetrying(id);
    try {
      const res: any = await retryFn({ data: { leadId: id } });
      const attempt: RetryAttempt = {
        leadId: id,
        leadEmail: email,
        ok: !!res?.ok,
        status: res?.status ?? 0,
        statusText: res?.statusText,
        body: res?.body ?? "",
        headers: res?.headers ?? {},
        startedAt: res?.startedAt ?? new Date().toISOString(),
        finishedAt: res?.finishedAt ?? new Date().toISOString(),
        error: res?.error,
      };
      setRetryLog((prev) => [attempt, ...prev].slice(0, 20));
      if (res?.ok) {
        toast.success(`GHL ${res.status} — delivered`);
      } else {
        toast.error(`GHL ${res?.status ?? 0} — ${res?.error ?? "failed"}`);
      }
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      const attempt: RetryAttempt = {
        leadId: id,
        leadEmail: email,
        ok: false,
        status: 0,
        body: "",
        headers: {},
        startedAt: new Date().toISOString(),
        finishedAt: new Date().toISOString(),
        error: e?.message ?? "Retry error",
      };
      setRetryLog((prev) => [attempt, ...prev].slice(0, 20));
      toast.error(e?.message ?? "Retry error");
    } finally {
      setRetrying(null);
    }
  }


  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/" });
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Leads</h1>
            <p className="text-sm text-muted-foreground">Every submission from your forms.</p>
          </div>
          <Button variant="outline" size="sm" onClick={signOut}>Sign out</Button>
        </header>

        <section className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Delivered (7d)" value={stats7d?.delivered} total={stats7d?.total} />
          <StatCard label="Failed (7d)" value={stats7d?.failed} total={stats7d?.total} />
          <StatCard label="Spam (7d)" value={stats7d?.spam_blocked} total={stats7d?.total} />
          <StatCard label="Total (30d)" value={stats30d?.total} />
        </section>

        <Card className="mb-4 flex flex-col gap-3 p-4 md:flex-row md:items-end">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Search</label>
            <Input
              placeholder="Name, email, business…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-full md:w-40">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Date range</label>
            <Select value={range} onValueChange={(v) => setRange(v as Range)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">Last 24 hours</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-44">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Status</label>
            <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="spam_blocked">Spam blocked</SelectItem>
                <SelectItem value="not_attempted">Not attempted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        <Card className="overflow-hidden">
          {error ? (
            <div className="p-6 text-sm text-destructive">{error}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Business</TableHead>
                  <TableHead>Page</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
                )}
                {!loading && leads.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">No leads match these filters.</TableCell></TableRow>
                )}
                {!loading && leads.map((l) => {
                  const badge = STATUS_BADGE[l.ghl_status] ?? { label: l.ghl_status, variant: "outline" as const };
                  return (
                    <TableRow key={l.id} className="cursor-pointer" onClick={() => setSelected(l)}>
                      <TableCell className="whitespace-nowrap text-xs">{fmtDate(l.submitted_at ?? l.created_at)}</TableCell>
                      <TableCell>{[l.first_name, l.last_name].filter(Boolean).join(" ") || "—"}</TableCell>
                      <TableCell className="text-xs">{l.email ?? "—"}</TableCell>
                      <TableCell className="text-xs">{l.phone ?? "—"}</TableCell>
                      <TableCell className="text-xs">{l.business_name ?? "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{l.page ?? "—"}</TableCell>
                      <TableCell><Badge variant={badge.variant}>{badge.label}</Badge></TableCell>
                      <TableCell className="text-right">
                        {(l.ghl_status === "failed" || l.ghl_status === "not_attempted") && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={retrying === l.id}
                            onClick={(e) => { e.stopPropagation(); onRetry(l.id, l.email); }}
                          >
                            {retrying === l.id ? "Retrying…" : "Retry"}
                          </Button>
                        )}
                      </TableCell>

                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        <Card className="mt-6 p-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">GHL retry attempts (session)</h2>
              <p className="text-xs text-muted-foreground">
                Full webhook response body + status for each Retry click. Clears on reload.
              </p>
            </div>
            {retryLog.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setRetryLog([])}>Clear</Button>
            )}
          </div>
          {retryLog.length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">
              No retries yet. Click <span className="font-medium">Retry</span> on a row above.
            </p>
          ) : (
            <ul className="space-y-3">
              {retryLog.map((a, i) => (
                <li key={`${a.leadId}-${a.startedAt}-${i}`} className="rounded border border-border p-3">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant={a.ok ? "default" : "destructive"}>
                      {a.status || "ERR"} {a.statusText ? `· ${a.statusText}` : ""}
                    </Badge>
                    <span className="font-medium">{a.leadEmail ?? a.leadId}</span>
                    <span className="text-muted-foreground">{fmtDate(a.finishedAt)}</span>
                    <span className="text-muted-foreground">
                      ({Math.max(0, new Date(a.finishedAt).getTime() - new Date(a.startedAt).getTime())} ms)
                    </span>
                  </div>
                  {a.error && (
                    <div className="mt-2 text-xs text-destructive">Error: {a.error}</div>
                  )}
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                      Response body ({a.body.length} chars)
                    </summary>
                    <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted p-2 text-xs">
{a.body || "(empty body)"}
                    </pre>
                  </details>
                  {Object.keys(a.headers).length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                        Response headers
                      </summary>
                      <pre className="mt-2 max-h-40 overflow-auto rounded bg-muted p-2 text-xs">
{Object.entries(a.headers).map(([k, v]) => `${k}: ${v}`).join("\n")}
                      </pre>
                    </details>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>


      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Lead details</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <Row label="Name" value={[selected.first_name, selected.last_name].filter(Boolean).join(" ") || "—"} />
              <Row label="Email" value={selected.email ?? "—"} />
              <Row label="Phone" value={selected.phone ?? "—"} />
              <Row label="Business" value={selected.business_name ?? "—"} />
              <Row label="Page" value={selected.page ?? "—"} />
              <Row label="Submitted" value={fmtDate(selected.submitted_at ?? selected.created_at)} />
              <Row label="Status" value={STATUS_BADGE[selected.ghl_status]?.label ?? selected.ghl_status} />
              {selected.ghl_delivered_at && <Row label="Delivered" value={fmtDate(selected.ghl_delivered_at)} />}
              {selected.ghl_error && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">GHL error</div>
                  <pre className="mt-1 max-h-32 overflow-auto rounded bg-muted p-2 text-xs">{selected.ghl_error}</pre>
                </div>
              )}
              <div>
                <div className="text-xs font-medium text-muted-foreground">Message</div>
                <p className="mt-1 whitespace-pre-wrap rounded bg-muted p-2">{selected.message ?? "—"}</p>
              </div>

              {lastByLead[selected.id] && (
                <div className="rounded border border-border p-3">
                  <div className="mb-2 flex items-center gap-2 text-xs">
                    <Badge variant={lastByLead[selected.id].ok ? "default" : "destructive"}>
                      {lastByLead[selected.id].status || "ERR"}
                    </Badge>
                    <span className="font-medium">Last retry this session</span>
                    <span className="text-muted-foreground">
                      {fmtDate(lastByLead[selected.id].finishedAt)}
                    </span>
                  </div>
                  <pre className="max-h-40 overflow-auto rounded bg-muted p-2 text-xs">
{lastByLead[selected.id].body || lastByLead[selected.id].error || "(empty)"}
                  </pre>
                </div>
              )}

              {(selected.ghl_status === "failed" || selected.ghl_status === "not_attempted") && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={retrying === selected.id}
                  onClick={() => onRetry(selected.id, selected.email)}
                >
                  {retrying === selected.id ? "Retrying…" : "Retry GHL delivery"}
                </Button>
              )}
            </div>
          )}

        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, total }: { label: string; value?: number; total?: number }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value ?? "—"}</div>
      {typeof total === "number" && total > 0 && typeof value === "number" && (
        <div className="text-xs text-muted-foreground">of {total}</div>
      )}
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3">
      <div className="w-24 shrink-0 text-xs font-medium text-muted-foreground">{label}</div>
      <div className="flex-1 break-all">{value}</div>
    </div>
  );
}
